// First import on purpose — installs the real Buffer polyfill before any
// aws-sdk module (pulled in transitively below) gets evaluated.
import "./polyfills";
import { hydrateRoot } from "react-dom/client";
import { createBrowserRouter } from "react-router";
import {
  RouterProvider,
} from "react-router/dom";
import { getAllRoutes } from "routes/all-routes";
import { CommonApp } from "index";
import reportWebVitals from './reportWebVitals';
import { createStore, RootState } from "redux/store";

declare global {
  interface Window {
    __PRELOADED_STATE__?: RootState;
    __staticRouterHydrationData?: {
      loaderData: Record<string, unknown>;
      actionData: Record<string, unknown> | null;
      errors: Record<string, unknown> | null;
    };
  }
}

const store = createStore(window.__PRELOADED_STATE__);
delete window.__PRELOADED_STATE__;
// Use lazy-loaded routes for code splitting
const allRoutes = getAllRoutes(store);
// Use hydration data from the server to prevent re-rendering
const router = createBrowserRouter(allRoutes, {
  hydrationData: window.__staticRouterHydrationData,
  }
);
delete window.__staticRouterHydrationData;
const root = document.getElementById('root')!;

// Pre-hydration DOM cleanup to fix React 19 hydration mismatch:
//
// Problem 1 - Emotion <style data-emotion> tags scattered throughout #root:
//   Emotion's streaming SSR injects <style data-emotion="..."> tags directly
//   before every styled component in the render stream (33+ tags total).
//   React's virtual DOM has NO <style> children anywhere — Emotion injects
//   styles via useInsertionEffect, not as React elements.
//   Every styled component causes a mismatch → React abandons the SSR tree,
//   client re-render creates new DOM alongside orphaned SSR DOM → duplication.
//
//   Fix: move ALL style[data-emotion] from anywhere in #root to <head>.
//   Emotion's createCache scans document.head for style[data-emotion] on init,
//   so it will still hydrate from these styles (no FOUC).
//   React now sees a clean DOM (only structural elements) → hydration succeeds.
//
// Problem 2 - React 19 hoistable metadata in #root:
//   React 19 server-renders <title>, <meta>, <link rel="preload"> as hoistable
//   elements, but renderToPipeableStream places them in #root (not <head>).
//   On the client React 19 re-creates them in <head> automatically.
//   Remove them from #root so hydrateRoot doesn't see unexpected leading nodes.
//
// Problem 3 - __staticRouterHydrationData <script> at end of #root:
//   StaticRouterProvider injects a <script> as the last child of #root.
//   After createBrowserRouter consumes it (above), the DOM node remains.
//   React sees an unexpected trailing <script> → hydration failure.

// Step 1: move ALL Emotion style tags from anywhere in #root to <head>
// (querySelectorAll returns a static snapshot — safe to iterate while moving)
root.querySelectorAll('style[data-emotion]').forEach(
  el => document.head.appendChild(el)
);

// Step 2: remove leading React 19 hoistable elements (title / meta / link)
// React 19 will re-inject these into <head> during hydration
let firstChild = root.firstElementChild;
while (firstChild && ['LINK', 'TITLE', 'META'].includes(firstChild.tagName)) {
  const next = firstChild.nextElementSibling;
  firstChild.remove();
  firstChild = next;
}

// Step 3: remove trailing __staticRouterHydrationData <script>
const lastChild = root.lastElementChild;
if (lastChild?.tagName === 'SCRIPT') {
  lastChild.remove();
}

// Hydration must not happen until the router is fully initialized.
//
// `createBrowserRouter` starts loading lazy route modules asynchronously.
// During that brief window router.state.initialized = false, and RouterProvider
// renders null (no HydrateFallback is available because it lives inside the
// lazy root module itself).  If hydrateRoot is called at that moment:
//
//   • SSR DOM contains:  HEADER + MAIN  (real article content)
//   • React vdom renders: null            (router still loading)
//   → hydration mismatch → React falls back to a fresh client mount
//   → React appends new HEADER+MAIN without removing the old SSR nodes
//   → duplicate content (2× header, 2× main)
//
// Fix: defer hydrateRoot until router.state.initialized = true.
// By then every lazy module for the current URL has been resolved, so
// RouterProvider renders the full component tree immediately.  The
// server-rendered HEADER+MAIN exactly matches the client vdom → clean hydration.
function hydrateApp() {
  hydrateRoot(
    root,
    <CommonApp store={store}>
      <RouterProvider router={router} />
    </CommonApp>
  );
  reportWebVitals();
}

if (router.state.initialized) {
  // Lazy modules were already cached / synchronously available.
  hydrateApp();
} else {
  // Wait for the router to finish loading lazy route modules for this URL,
  // then hydrate.  The SSR content remains visible during this brief wait.
  const unsubscribe = router.subscribe(state => {
    if (state.initialized) {
      unsubscribe();
      hydrateApp();
    }
  });
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
